import Button from '@components/v2/ui/Button/Button';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import LangEnum from '../../lang/LangEnum';
import { useApplicationContext } from '../../modules/context/ApplicationContext/ApplicationContext';
import { MediaFormatEnum } from '../../modules/general-settings/enums/mediaFormat.enum';
import { useOrderProductsContext } from '../../modules/orders/contexts/OrderProducts.context';
import { OrderProduct } from '../../modules/orders/products/orderProducts.types';
import ProductAvatarRenderer from '../../modules/products/ProductAvatarRenderer';
import { useTranslation } from '../../modules/translation/TranslationContext';
import { getFindProductGridKey } from '../../services/modules/product-grid/product-grid.keys';
import useGeneralSettingsStore from '../../stores/GeneralSettings/useGeneralSettingsStore';
import useOrderBookStore from '../../stores/OrderBook/orderBook.store';
import useProductActionsStore from '../../stores/ProductActionsStore/productActions.store';
import { MediaType } from '../../types/general.types';
import useWidth from '../Hooks/useWidth';
import ProductNotificationSnackbar from './ProductNotificationSnackbar';
import { useProductNotificationStyles } from './styles/ProductNotificationStyles';

export const contentNotificationHeight: Record<MediaFormatEnum, number> = {
  [MediaFormatEnum.SQUARE]: 65,
  [MediaFormatEnum.RECTANGULAR]: 80,
};

export const contentNotificationImageWidth: Record<MediaFormatEnum, number | string> = {
  [MediaFormatEnum.SQUARE]: 65,
  [MediaFormatEnum.RECTANGULAR]: 55,
};

const COLOR_DOT_SIZE = 12;
const PRODUCT_TEXT_MAX_WIDTH = 210;

const ProductAddNotification = () => {
  const {
    closeProductNotification,
    productNotificationState,
    orderProducts,
    resetProductActionsStore,
    setNotificationTimerIsValid,
  } = useProductActionsStore(
    useShallow((state) => ({
      closeProductNotification: state.closeProductNotification,
      productNotificationState: state.productNotificationState,
      orderProducts: state.orderProducts,
      resetProductActionsStore: state.resetProductActionsStore,
      setNotificationTimerIsValid: state.setNotificationTimerIsValid,
    })),
  );

  const { onClickRemove, pushOrderProducts, onClickRemoveKitFromOrder } = useOrderProductsContext();
  const { translate } = useTranslation();
  const mediaFormat = useGeneralSettingsStore((state) => state.generalSettings.mediaFormat);
  const { classes } = useProductNotificationStyles({ mediaFormat });
  const { mobileWidth } = useWidth();
  const queryClient = useQueryClient();
  const { team } = useApplicationContext();
  const selectedOrder = useOrderBookStore((state) => state.selectedOrder);

  const id = productNotificationState.productAdd ? 'simple-snackbar' : undefined;
  const valueOfX = mobileWidth ? 14 : 0;
  const newValueOfX = productNotificationState.productAdd ? valueOfX : 400;

  const product = orderProducts?.[0];
  const productName = product?.kit?.name || product?.name;
  const productCode = product?.kit?.code || product?.code;
  const contentHeight = contentNotificationHeight[mediaFormat];
  const url =
    product?.kit?.imageUrl ||
    (product?.media?.type !== MediaType.VIDEO ? product?.media?.url : undefined);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!orderProducts?.length || productNotificationState.isOrderProductsApplied) {
      return;
    }

    orderProducts.forEach((product) => {
      queryClient.removeQueries({
        queryKey: [
          team?.id,
          ...getFindProductGridKey({
            productColorId: product.productColorId,
            availability: product.availability,
            deliveryWindowId: product?.deliveryWindow?.id ?? '',
            orderId: selectedOrder?.id,
          }),
        ],
      });
    });
    pushOrderProducts(orderProducts);
  }, [orderProducts, queryClient.removeQueries, selectedOrder?.id, team?.id]);

  const handleRemoveProduct = async () => {
    if (!product) {
      return;
    }
    closeProductNotification();

    if (product.kit?.id) {
      onClickRemoveKitFromOrder(product.kit?.id);
    } else {
      onClickRemove(product as OrderProduct, 0);
    }

    resetProductActionsStore();
    setNotificationTimerIsValid(false);
  };

  return (
    <ProductNotificationSnackbar
      onClose={(_, reason) => reason === 'timeout' && setNotificationTimerIsValid(false)}
      autoHideDuration={5000}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={id}
      className={classes.snackbar}
      open={productNotificationState.productAdd}
      initial={{ opacity: 1, x: 400, y: 0 }}
      animate={{ opacity: 1, x: newValueOfX, y: 0 }}
      exit={{ opacity: 1, x: 400, y: 0 }}
    >
      <div style={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {translate(LangEnum.PRODUCT_NOTIFICATION_ADD)}
          </Typography>

          <Button variant="text" color="primary" size="small" onClick={handleRemoveProduct}>
            {translate(LangEnum.PRODUCT_NOTIFICATION_UNDO)}
          </Button>
        </Box>

        <Box
          sx={(theme) => ({
            display: 'flex',
            width: '100%',
            height: contentHeight,
            borderRadius: '8px',
            border: `1px solid ${theme.palette.custom.border.primary}`,
            overflow: 'hidden',
          })}
        >
          <Box sx={{ flexShrink: 0, marginRight: 1 }}>
            <ProductAvatarRenderer
              url={url}
              className={classes.avatarContainer}
              style={{ height: contentHeight }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 0.5,
              minWidth: 0,
              height: contentHeight,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                variant="body2"
                color="textPrimary"
                title={productName}
                sx={{ maxWidth: PRODUCT_TEXT_MAX_WIDTH }}
              >
                {productName}
              </Typography>
              <Typography
                noWrap
                variant="micro1"
                color="text.secondary"
                title={productCode}
                sx={{ maxWidth: PRODUCT_TEXT_MAX_WIDTH }}
              >
                {productCode}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <Box
                sx={(theme) => ({
                  width: COLOR_DOT_SIZE + 2,
                  height: COLOR_DOT_SIZE + 2,
                  flexShrink: 0,
                  borderRadius: '50%',
                  backgroundColor: product?.color?.hexcode,
                  border: `1px solid ${theme.palette.custom.border.primary}`,
                })}
              />
              <Typography
                noWrap
                variant="micro1"
                color="text.secondary"
                className={classes.colorName}
              >
                {product?.color?.name || ''}
              </Typography>
            </Box>
          </Box>
        </Box>
      </div>
    </ProductNotificationSnackbar>
  );
};

export default ProductAddNotification;
